import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { publishedService, revisionService } from "../../services/firestoreService";
import {
  FiArrowLeft,
  FiX
} from "react-icons/fi";
import Portal from "../../components/Portal";
import { useLanguage } from "../../contexts/LanguageContext";
import "./staffcss/contentdashboard.css";

const ContentViewRevision = () => {
  const { getTranslations } = useLanguage();
  const t = getTranslations(false, true);
  const { contentId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [originalContent, setOriginalContent] = useState(null);
  const [revisionData, setRevisionData] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewDevice, setPreviewDevice] = useState("desktop"); // 'mobile' | 'desktop'
  const [isEditSocialOpen, setIsEditSocialOpen] = useState(false);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  const [highlightedRevisionContent, setHighlightedRevisionContent] = useState("");

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get original content from published collection
      let original = null;
      try {
        original = await publishedService.getPublishedByContentId(contentId);
        if (original) {
          setOriginalContent(original);
        }
      } catch (originalError) {
        console.error('ContentViewRevision: Error fetching original content:', originalError);
        setError('Failed to load original content. Please try again.');
      }
      
      // Get revision from revisions collection
      let revision = null;
      try {
        revision = await revisionService.getRevisionByContentId(contentId);
        if (revision) {
          setRevisionData(revision);
        }
      } catch (revisionError) {
        console.error('ContentViewRevision: Error fetching revision data:', revisionError);
        if (!error) { // Only set error if original content fetch succeeded
          setError('Failed to load revision data. Please try again.');
        }
      }
      
      // Check if both reads failed
      if (!original && !revision) {
        setError('No content found. The content may have been deleted or you may not have access.');
      } else if (!original) {
        // Show warning but still display revision
        setWarning('Original content not found, but revision data is available.');
      } else if (!revision) {
        // Show warning but still display original
        console.warn('ContentViewRevision: Revision data not found, but original content is available');
        setWarning('Revision data not found, but original content is available.');
      }
    } catch (error) {
      console.error("ContentViewRevision: Unexpected error fetching data:", error);
      setError('An unexpected error occurred while loading content. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [contentId]);

  useEffect(() => {
    fetchData();
  }, [contentId]);

  // Apply highlighting when revision data is loaded
  useEffect(() => {
    if (revisionData && originalContent) {
      const highlightedHtml = highlightChanges(originalContent.bodyHtml || "", revisionData.bodyHtml || "");
      setHighlightedRevisionContent(highlightedHtml);
    } else if (revisionData) {
      // If no original content, just use the revision content as-is
      setHighlightedRevisionContent(revisionData.bodyHtml || "");
    }
  }, [revisionData, originalContent]);

  const stripHtmlToText = (html) => {
    try {
      const div = document.createElement("div");
      div.innerHTML = html || "";
      return (div.textContent || div.innerText || "").replace(/\s+/g, " ").trim();
    } catch {
      return "";
    }
  };

  const highlightChanges = (originalHtml, revisedHtml) => {
    if (!originalHtml || !revisedHtml) return revisedHtml;
    
    // Don't highlight if already highlighted
    if (revisedHtml.includes('<mark')) return revisedHtml;
    
    // Simple character-by-character diff
    const originalText = stripHtmlToText(originalHtml);
    const revisedText = stripHtmlToText(revisedHtml);
    
    if (originalText === revisedText) return revisedHtml;
    
    // Find contiguous changed segments
    let result = '';
    let i = 0, j = 0;
    let inChange = false;
    
    while (i < originalText.length && j < revisedText.length) {
      if (originalText[i] === revisedText[j]) {
        if (inChange) {
          result += '</mark>';
          inChange = false;
        }
        result += revisedText[j];
        i++;
        j++;
      } else {
        if (!inChange) {
          result += '<mark style="background-color: #dbeafe; padding: 2px 4px; border-radius: 4px; border: 1px solid #bfdbfe; color: #0c4a6e;">';
          inChange = true;
        }
        result += revisedText[j];
        j++;
      }
    }
    
    // Handle remaining characters
    if (j < revisedText.length) {
      if (!inChange) {
        result += '<mark style="background-color: #dbeafe; padding: 2px 4px; border-radius: 4px; border: 1px solid #bfdbfe; color: #0c4a6e;">';
      }
      result += revisedText.substring(j);
    }
    
    if (inChange) {
      result += '</mark>';
    }
    
    // If no changes were found, return original
    if (!result.includes('<mark')) return revisedHtml;
    
    return result;
  };

  if (error) {
    return (
      <div className="cc-add-content-container">
        <div className="error-state" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          padding: '32px',
          textAlign: 'center'
        }}>
          <h3 style={{ color: '#ef4444', marginBottom: '12px' }}>Error Loading Content</h3>
          <p style={{ color: '#64748b', marginBottom: '24px' }}>{error}</p>
          <button 
            onClick={() => navigate('/content-curator?view=library')}
            style={{
              background: 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 24px',
              fontWeight: '600',
              cursor: 'pointer',
              marginTop: '16px'
            }}
          >
            Back to Library
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="cc-add-content-container">
      <style>{`
        .admin-aura-skeleton {
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
          border-radius: 4px;
        }
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .admin-aura-skeleton-text {
          height: 16px;
          margin-bottom: 8px;
        }
        .admin-aura-skeleton-title {
          height: 32px;
          width: 60%;
          margin-bottom: 16px;
        }
        .admin-aura-skeleton-input {
          height: 40px;
          width: 100%;
        }
        .admin-aura-skeleton-editor {
          height: 300px;
          width: 100%;
        }
      `}</style>
      {/* Warning Banner */}
      {warning && (
        <div style={{
          background: '#fef3c7',
          border: '1px solid #f59e0b',
          borderRadius: '8px',
          padding: '12px 16px',
          margin: '16px 24px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <span style={{ color: '#b45309', fontWeight: '600' }}>⚠️</span>
          <span style={{ color: '#b45309', fontSize: '14px' }}>{warning}</span>
        </div>
      )}
      
      {/* Top Header Bar with System Branding (Fixed) */}
      <header className="cc-top-header" style={{ 
        background: 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)',
        boxShadow: '0 4px 20px rgba(15, 23, 42, 0.3)',
        padding: '14px 32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '24px',
        border: 'none',
        borderRadius: '0'
      }}>
        <div className="cc-header-left" style={{ display: 'flex', alignItems: 'center', gap: '16px', margin: 0, padding: 0 }}>
          <button className="cc-back-btn" 
            onClick={() => navigate(`/article-view?id=${revisionData?.originalApprovalId || originalContent?.contentId || contentId}`)}
            style={{ 
              background: 'rgba(255,255,255,0.15)', 
              color: '#fff', 
              borderRadius: '8px',
              border: '1px solid rgba(255,255,255,0.2)',
              padding: '8px 12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: 0
            }}
          >
            <FiArrowLeft style={{ fontSize: '16px' }} />
          </button>
          <div className="cc-saved-status" style={{ 
            cursor: 'default', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '10px',
            padding: '8px 14px',
            background: 'rgba(255,255,255,0.08)',
            borderRadius: '8px',
            border: '1px solid rgba(255,255,255,0.12)',
            margin: 0
          }}>
            <div className="cc-status-dot saved" style={{ 
              background: '#34d399', 
              width: '8px', 
              height: '8px', 
              borderRadius: '50%',
              margin: 0
            }}></div>
            <span style={{ 
              color: '#f8fafc', 
              fontWeight: '600', 
              fontSize: '14px'
            }}>Viewing Proposed Revision</span>
          </div>
        </div>
        
        <div className="cc-header-right" style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '12px',
          margin: 0,
          padding: 0
        }}>
          <div className="cc-publish-section" style={{ 
            display: 'flex', 
            alignItems: 'center',
            margin: 0,
            padding: 0
          }}>
            {loading ? (
              <div className="admin-aura-skeleton" style={{
                padding: '8px 16px',
                borderRadius: '8px',
                width: '150px',
                height: '36px'
              }} />
            ) : (
              <div style={{
                padding: '8px 16px',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: '#fff',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '14px',
                margin: 0
              }}>
                Category: {revisionData?.category || originalContent?.category || "General Health"}
              </div>
            )}
          </div>
          <button
            className="cc-preview-btn"
            type="button"
            onClick={() => {
              setPreviewDevice("desktop");
              setIsPreviewOpen(true);
            }}
            style={{ 
              background: 'rgba(255,255,255,0.15)', 
              color: '#fff', 
              borderRadius: '8px',
              border: '1px solid rgba(255,255,255,0.2)',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '600',
              margin: 0
            }}
          >
            Preview
          </button>
          <button
            className="cc-preview-btn"
            type="button"
            onClick={() => setIsEditSocialOpen(true)}
            style={{ 
              background: 'rgba(255,255,255,0.15)', 
              color: '#fff', 
              borderRadius: '8px',
              border: '1px solid rgba(255,255,255,0.2)',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '600',
              margin: 0
            }}
          >
            Social Preview
          </button>
        </div>
      </header>

      {/* Editor Area - Split View (Refined) */}
      <div style={{ 
        display: 'flex', 
        flexDirection: 'row',
        width: '100%',
        maxWidth: '1400px',
        margin: '0 auto',
        gap: '24px', 
        padding: '24px',
        background: 'linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)',
        minHeight: 'calc(100vh - 80px)',
        overflowY: 'auto'
      }}>
        {/* Original Content - Read Only */}
        <div style={{ 
          width: 'calc(50% - 12px)',
          minWidth: 0,
          background: '#fff',
          borderRadius: '16px',
          padding: '24px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          border: '1px solid #e2e8f0',
          overflowY: 'auto',
          minHeight: '520px',
          maxHeight: 'none'
        }}>
          <div style={{ 
            background: 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
            padding: '16px 20px',
            borderRadius: '12px',
            marginBottom: '20px'
          }}>
            <h3 style={{ margin: 0, color: '#fff', fontWeight: 700, fontSize: '16px' }}>Original Version (Published)</h3>
          </div>
          
          {loading ? (
            <>
              <div className="admin-aura-skeleton admin-aura-skeleton-input" style={{ height: '50px', marginBottom: '16px' }} />
              <div className="admin-aura-skeleton admin-aura-skeleton-input" style={{ height: '40px', marginBottom: '20px' }} />
              <div className="admin-aura-skeleton admin-aura-skeleton-editor" />
            </>
          ) : (
            <>
              <input
                type="text"
                className="cc-title"
                placeholder="Title"
                value={originalContent?.title || ""}
                readOnly
                style={{ 
                  backgroundColor: '#f8fafc',
                  borderRadius: '10px',
                  border: '1px solid #e2e8f0',
                  padding: '12px 16px',
                  fontSize: '20px',
                  fontWeight: 700,
                  width: '100%',
                  marginBottom: '16px'
                }}
              />
              
              <input
                type="text"
                className="cc-subtitle"
                placeholder="Add a subtitle..."
                value={originalContent?.subtitle || ""}
                readOnly
                style={{ 
                  backgroundColor: '#f8fafc',
                  borderRadius: '10px',
                  border: '1px solid #e2e8f0',
                  padding: '10px 14px',
                  fontSize: '16px',
                  width: '100%',
                  marginBottom: '20px'
                }}
              />
              
              <div
                className="cc-text-editor"
                style={{ 
                  backgroundColor: '#f8fafc', 
                  padding: '20px',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  overflowY: 'auto',
                  minHeight: '300px'
                }}
                dangerouslySetInnerHTML={{ __html: originalContent?.bodyHtml || "" }}
              />
            </>
          )}
        </div>

        {/* Proposed Revision - Read Only */}
        <div style={{ 
          width: 'calc(50% - 12px)',
          minWidth: 0,
          background: '#fff',
          borderRadius: '16px',
          padding: '24px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          border: '1px solid #e2e8f0',
          overflowY: 'auto',
          minHeight: '520px',
          maxHeight: 'none'
        }}>
          <div style={{ 
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            padding: '16px 20px',
            borderRadius: '12px',
            marginBottom: '20px'
          }}>
            <h3 style={{ margin: 0, color: '#fff', fontWeight: 700, fontSize: '16px' }}>Proposed Revision</h3>
          </div>
          
          {loading ? (
            <>
              <div className="admin-aura-skeleton admin-aura-skeleton-input" style={{ height: '50px', marginBottom: '16px' }} />
              <div className="admin-aura-skeleton admin-aura-skeleton-input" style={{ height: '40px', marginBottom: '20px' }} />
              <div className="admin-aura-skeleton admin-aura-skeleton-editor" />
            </>
          ) : (
            <>
              <input
                type="text"
                className="cc-title"
                placeholder="Title"
                value={revisionData?.title || ""}
                readOnly
                style={{ 
                  backgroundColor: '#f0fdf4',
                  borderRadius: '10px',
                  border: '1px solid #bbf7d0',
                  padding: '12px 16px',
                  fontSize: '20px',
                  fontWeight: 700,
                  width: '100%',
                  marginBottom: '16px'
                }}
              />
              
              <input
                type="text"
                className="cc-subtitle"
                placeholder="Add a subtitle..."
                value={revisionData?.subtitle || ""}
                readOnly
                style={{ 
                  backgroundColor: '#f0fdf4',
                  borderRadius: '10px',
                  border: '1px solid #bbf7d0',
                  padding: '10px 14px',
                  fontSize: '16px',
                  width: '100%',
                  marginBottom: '20px'
                }}
              />
              
              <div
                className="cc-text-editor"
                style={{ 
                  backgroundColor: '#f0fdf4', 
                  padding: '20px',
                  borderRadius: '12px',
                  border: '1px solid #bbf7d0',
                  overflowY: 'auto',
                  minHeight: '300px'
                }}
                dangerouslySetInnerHTML={{ __html: highlightedRevisionContent || revisionData?.bodyHtml || "" }}
              />
            </>
          )}
        </div>
      </div>

      {/* Live Preview Modal with System Branding */}
      {isPreviewOpen && (
        <Portal>
          <div className="cc-preview-overlay" style={{ background: 'rgba(15, 23, 42, 0.85)' }}>
          <div className="cc-preview-modal" style={{ borderRadius: '16px', overflow: 'hidden' }}>
            <header className="cc-preview-header" style={{ 
              background: 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)',
              color: '#fff'
            }}>
              <div className="cc-preview-tabs">
                <button
                  type="button"
                  className={previewDevice === "mobile" ? "active" : ""}
                  onClick={() => setPreviewDevice("mobile")}
                  style={{ 
                    color: previewDevice === "mobile" ? '#fff' : '#94a3b8',
                    background: previewDevice === "mobile" ? 'rgba(59, 130, 246, 0.3)' : 'transparent',
                    border: previewDevice === "mobile" ? '1px solid rgba(59, 130, 246, 0.5)' : 'none'
                  }}
                >
                  Mobile
                </button>
                <button
                  type="button"
                  className={previewDevice === "desktop" ? "active" : ""}
                  onClick={() => setPreviewDevice("desktop")}
                  style={{ 
                    color: previewDevice === "desktop" ? '#fff' : '#94a3b8',
                    background: previewDevice === "desktop" ? 'rgba(59, 130, 246, 0.3)' : 'transparent',
                    border: previewDevice === "desktop" ? '1px solid rgba(59, 130, 246, 0.5)' : 'none'
                  }}
                >
                  Desktop
                </button>
              </div>
              <button
                type="button"
                className="cc-preview-done"
                onClick={() => setIsPreviewOpen(false)}
                style={{ 
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  color: '#fff',
                  borderRadius: '10px',
                  border: 'none'
                }}
              >
                Done
              </button>
            </header>

            <div className="cc-preview-body">
              {previewDevice === "mobile" && (
                <div className="cc-preview-mobile-frame">
                  <div className="cc-preview-mobile-notch" />
                  <div className="cc-preview-mobile-content">
                    <h1>{revisionData?.title || "Article Title"}</h1>
                    {revisionData?.subtitle && <h2>{revisionData?.subtitle}</h2>}
                    <div dangerouslySetInnerHTML={{ __html: highlightedRevisionContent || revisionData?.bodyHtml || "" }} />
                  </div>
                </div>
              )}
              {previewDevice === "desktop" && (
                <div className="cc-preview-desktop-content">
                  <h1>{revisionData?.title || "Article Title"}</h1>
                  {revisionData?.subtitle && <h2>{revisionData?.subtitle}</h2>}
                  <div dangerouslySetInnerHTML={{ __html: highlightedRevisionContent || revisionData?.bodyHtml || "" }} />
                </div>
              )}
            </div>
          </div>
        </div>
        </Portal>
      )}

      {/* Edit social preview modal (read only) with System Branding */}
      {isEditSocialOpen && (
        <Portal>
          <div className="cc-edit-social-overlay" role="dialog" aria-modal="true" style={{ background: 'rgba(15, 23, 42, 0.85)' }}>
          <div className="cc-edit-social-modal" style={{ borderRadius: '16px', overflow: 'hidden' }}>
            <header className="cc-edit-social-header" style={{ 
              background: 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)',
              color: '#fff'
            }}>
              <div>
                <div className="cc-edit-social-title" style={{ color: '#fff' }}>Social Preview</div>
                <div className="cc-edit-social-subtitle" style={{ color: '#cbd5e1' }}>
                  How this post would appear on other platforms.
                </div>
              </div>
              <button
                type="button"
                className="cc-edit-social-close"
                onClick={() => setIsEditSocialOpen(false)}
                aria-label="Close social preview"
                title="Close"
                style={{ 
                  background: 'rgba(255,255,255,0.1)',
                  color: '#fff',
                  borderRadius: '10px'
                }}
              >
                <FiX />
              </button>
            </header>

            <div className="cc-edit-social-body">
              <label className="cc-edit-field">
                <span>Title</span>
                <input
                  type="text"
                  value={revisionData?.socialTitle || revisionData?.title || ""}
                  readOnly
                  style={{ backgroundColor: '#f1f5f9' }}
                />
              </label>

              <label className="cc-edit-field">
                <span>Description</span>
                <textarea
                  value={revisionData?.socialDescription || ""}
                  readOnly
                  rows={4}
                  style={{ backgroundColor: '#f1f5f9' }}
                />
              </label>

              <div className="cc-edit-image-section">
                <div className="cc-edit-image-label">Image</div>
                <div className="cc-edit-image-box" style={{ cursor: 'default' }}>
                  {(revisionData?.socialImageUrl || originalContent?.socialImageUrl) ? (
                    <img src={revisionData?.socialImageUrl || originalContent?.socialImageUrl} alt="" />
                  ) : (
                    <div className="cc-edit-image-placeholder">
                      No image available
                    </div>
                  )}
                </div>
              </div>
            </div>

            <footer className="cc-edit-social-footer">
              <button type="button" 
                className="cc-edit-cancel" 
                onClick={() => setIsEditSocialOpen(false)}
                style={{ 
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  color: '#fff',
                  borderRadius: '10px',
                  border: 'none',
                  padding: '12px 24px',
                  fontWeight: 600,
                  fontSize: '14px'
                }}
              >
                Close
              </button>
            </footer>
          </div>
        </div>
        </Portal>
      )}
    </div>
  );
};

export default ContentViewRevision;
